// Single source of truth for classifying a project file by extension. Both the
// type icon (fileTypeIcon.tsx) and the preview router (filePreview.ts) derive
// from this, so adding an extension in one place updates both and they can't
// drift. Categories are fine-grained on purpose: csv/tsv/spreadsheet share an
// icon but route to different previews, and code files preview as text.

export type FileCategory =
  | "image"
  | "pdf"
  | "word"
  | "slides"
  | "spreadsheet"
  | "csv"
  | "tsv"
  | "text"
  | "schedule"
  | "archive"
  | "mail"
  | "other";

const EXT_CATEGORY: Record<string, FileCategory> = {
  // images (svg included — browsers render it in <img>)
  png: "image",
  jpg: "image",
  jpeg: "image",
  gif: "image",
  webp: "image",
  bmp: "image",
  tiff: "image",
  svg: "image",
  // documents
  pdf: "pdf",
  doc: "word",
  docx: "word",
  rtf: "word",
  odt: "word",
  ppt: "slides",
  pptx: "slides",
  // tabular
  xls: "spreadsheet",
  xlsx: "spreadsheet",
  ods: "spreadsheet",
  csv: "csv",
  tsv: "tsv",
  xer: "schedule",
  // plain text and config
  txt: "text",
  md: "text",
  log: "text",
  json: "text",
  yaml: "text",
  yml: "text",
  xml: "text",
  html: "text",
  ini: "text",
  toml: "text",
  env: "text",
  conf: "text",
  // code (previews and icons as text)
  js: "text",
  jsx: "text",
  ts: "text",
  tsx: "text",
  py: "text",
  sh: "text",
  bash: "text",
  zsh: "text",
  css: "text",
  scss: "text",
  sql: "text",
  java: "text",
  c: "text",
  h: "text",
  cpp: "text",
  hpp: "text",
  cs: "text",
  go: "text",
  rs: "text",
  rb: "text",
  php: "text",
  // archives and mail
  zip: "archive",
  tar: "archive",
  gz: "archive",
  "7z": "archive",
  rar: "archive",
  eml: "mail",
  msg: "mail",
};

export function getFileCategory(
  fileName: string,
  contentType?: string,
): FileCategory {
  const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
  if (EXT_CATEGORY[ext]) return EXT_CATEGORY[ext];

  // Fall back to the MIME type when the name has no useful extension.
  if (contentType?.includes("pdf")) return "pdf";
  if (contentType?.startsWith("image/")) return "image";
  if (contentType?.includes("spreadsheet") || contentType?.includes("excel"))
    return "spreadsheet";
  if (contentType?.includes("presentation")) return "slides";
  if (contentType?.includes("document")) return "word";
  if (contentType?.startsWith("text/")) return "text";
  return "other";
}
